import { App } from 'antd';
import { useMsal } from '@azure/msal-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CalendarSlot,
  PreSalesFieldSalesCalendarSlot,
  PreSalesFieldSalesSlot,
  Slot,
} from '../../../dto/model';
import { scopes } from '../../../authConfig';
import { strings } from '../../../lang';
import {
  heatBookingPageService,
  mapPreSalesFieldSalesSlots,
} from '../../../services/heat-booking-page-service';
import { GetAccessToken } from '../../../utils/auth-utils';

type UsePreSalesFieldSalesBookingParams = {
  crmId?: string;
  /**
   * The identifier PreSales books the field-sales slot against. Defaults to {@link crmId}; the unified (ad)
   * flow has no deal id, so it passes the signup id instead.
   */
  dealId?: string;
  zipCode?: string;
};

export const usePreSalesFieldSalesBooking = ({
  crmId,
  dealId,
  zipCode,
}: UsePreSalesFieldSalesBookingParams) => {
  // PreSales keys the field-sales booking on a deal id. The unified flow has no deal, so it supplies the
  // signup id through dealId and that is what travels as dealId on the wire.
  const bookingId = dealId ?? crmId;
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [bookedSlot, setBookedSlot] = useState<Slot | null>(null);
  const [hasConfirmedAgentOnly, setHasConfirmedAgentOnly] = useState(false);
  const [isConfirmingAgentOnly, setIsConfirmingAgentOnly] = useState(false);
  const confirmationSeqRef = useRef(0);
  const slotsCacheRef = useRef<{ zipCode: string; slots: PreSalesFieldSalesSlot[] } | null>(null);
  const slotsRequestRef = useRef<{
    zipCode: string;
    promise: Promise<PreSalesFieldSalesSlot[]>;
  } | null>(null);
  const pendingBookingRef = useRef<{ dealId: string; slot: Slot } | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      confirmationSeqRef.current += 1;
    };
  }, []);

  useEffect(() => {
    slotsCacheRef.current = null;
    slotsRequestRef.current = null;
  }, [zipCode]);

  const reset = useCallback(() => {
    confirmationSeqRef.current += 1;
    slotsRequestRef.current = null;
    pendingBookingRef.current = null;
    setBookedSlot(null);
    setHasConfirmedAgentOnly(false);
    setIsConfirmingAgentOnly(false);
  }, []);

  useEffect(() => {
    reset();
  }, [bookingId, reset]);

  const clearResult = useCallback(() => {
    setBookedSlot(null);
    setHasConfirmedAgentOnly(false);
  }, []);

  const cancelAgentOnlyConfirmation = useCallback(() => {
    confirmationSeqRef.current += 1;
    setIsConfirmingAgentOnly(false);
    setHasConfirmedAgentOnly(false);
  }, []);

  const confirmAgentOnly = useCallback(async () => {
    if (!bookingId) {
      notification.error({ message: strings.preSalesQuestionnaire.fieldSalesConfirmationMissingDeal });
      return;
    }

    const confirmationSeq = ++confirmationSeqRef.current;
    setIsConfirmingAgentOnly(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const confirmation = await heatBookingPageService.confirmPreSalesFieldSalesBooking(bookingId, token.accessToken);
      if (!isMountedRef.current || confirmationSeq !== confirmationSeqRef.current) {
        return;
      }

      if (!confirmation.success) {
        notification.error({
          message: strings.preSalesQuestionnaire.fieldSalesConfirmationError,
          description: confirmation.error ?? confirmation.message,
        });
        return;
      }

      setHasConfirmedAgentOnly(true);
      notification.success({ message: strings.preSalesQuestionnaire.fieldSalesBookingSuccess });
    } catch (error: any) {
      if (isMountedRef.current && confirmationSeq === confirmationSeqRef.current) {
        notification.error({
          message: strings.preSalesQuestionnaire.fieldSalesConfirmationError,
          description: error?.message,
        });
      }
    } finally {
      if (isMountedRef.current && confirmationSeq === confirmationSeqRef.current) {
        setIsConfirmingAgentOnly(false);
      }
    }
  }, [bookingId, inProgress, instance, notification]);

  const loadSlots = useCallback(async (calendarWeek: string, accessToken: string) => {
    if (!zipCode) {
      throw new Error(strings.preSalesQuestionnaire.fieldSalesMissingZipCode);
    }

    const cachedSlots = slotsCacheRef.current;
    if (cachedSlots?.zipCode === zipCode) {
      return {
        slots: mapPreSalesFieldSalesSlots(cachedSlots.slots, calendarWeek),
      };
    }

    let request = slotsRequestRef.current;
    if (request?.zipCode !== zipCode) {
      const promise = heatBookingPageService.getPreSalesFieldSalesSlots(zipCode, accessToken).then((response) => {
        if (!response.success) {
          throw new Error(response.error ?? response.message ?? strings.preSalesQuestionnaire.fieldSalesSlotsError);
        }

        return response.data ?? [];
      });
      request = { zipCode, promise };
      slotsRequestRef.current = request;
    }

    try {
      const slots = await request.promise;
      if (slotsRequestRef.current === request) {
        slotsCacheRef.current = { zipCode, slots };
        slotsRequestRef.current = null;
      }

      return {
        slots: mapPreSalesFieldSalesSlots(slots, calendarWeek),
      };
    } catch (error) {
      if (slotsRequestRef.current === request) {
        slotsRequestRef.current = null;
      }
      throw error;
    }
  }, [zipCode]);

  const bookSlot = useCallback(async (slot: CalendarSlot, accessToken: string) => {
    const slotId = slot.externalSlotId;
    if (!bookingId || !zipCode || !slotId) {
      return {
        isSuccess: false,
        errors: [strings.preSalesQuestionnaire.fieldSalesBookingMissingData],
        data: {} as Slot,
      };
    }

    let nextBookedSlot = pendingBookingRef.current?.dealId === bookingId
      ? pendingBookingRef.current.slot
      : null;

    if (!nextBookedSlot) {
      const response = await heatBookingPageService.bookPreSalesFieldSalesSlot(
        {
          dealId: bookingId,
          slotId,
          zipCode,
        },
        accessToken,
      );

      if (!response.success) {
        return {
          isSuccess: false,
          errors: [response.error ?? response.message ?? strings.preSalesQuestionnaire.fieldSalesBookingError],
          data: {} as Slot,
        };
      }

      nextBookedSlot = {
        id: 0,
        startDate: slot.startDate,
        // The calendar carries the real appointment end; on-site slots are not a fixed length.
        endDate: slot.endDate ?? slot.startDate.add(1, 'hour'),
        salesManagerName: slot.salesManagerName ?? '',
        externalType: 'FieldSales',
        externalStatus: 'Booked',
        meetingUrl: '',
        metaBookingInfo: '',
        externalId: response.data?.slotId ?? slotId,
      };
      pendingBookingRef.current = { dealId: bookingId, slot: nextBookedSlot };
    }

    const confirmation = await heatBookingPageService.confirmPreSalesFieldSalesBooking(bookingId, accessToken);
    if (!confirmation.success) {
      return {
        isSuccess: false,
        errors: [confirmation.error ?? confirmation.message ?? strings.preSalesQuestionnaire.fieldSalesBookingConfirmationError],
        data: nextBookedSlot,
      };
    }

    pendingBookingRef.current = null;
    return {
      isSuccess: true,
      errors: [],
      data: nextBookedSlot,
    };
  }, [bookingId, zipCode]);

  const handleBooking = useCallback((isSuccess: boolean, nextBookedSlot?: Slot, error?: string) => {
    if (!isMountedRef.current) {
      return;
    }

    if (isSuccess && nextBookedSlot) {
      setBookedSlot({
        ...nextBookedSlot,
        startDate: dayjs(nextBookedSlot.startDate),
        endDate: dayjs(nextBookedSlot.endDate),
      });
      notification.success({ message: strings.preSalesQuestionnaire.fieldSalesBookingSuccess });
      return;
    }

    if (error) {
      notification.error({
        message: strings.preSalesQuestionnaire.fieldSalesBookingError,
        description: error,
      });
    }
  }, [notification]);

  return {
    bookedSlot,
    hasConfirmedAgentOnly,
    isConfirmingAgentOnly,
    loadSlots,
    bookSlot,
    handleBooking,
    confirmAgentOnly,
    cancelAgentOnlyConfirmation,
    clearResult,
    hasPendingBooking: () => pendingBookingRef.current !== null,
  };
};
